import { useState } from "react";
import { FormControlLabel, IconButton, InputAdornment, Stack, Switch, TextField } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useField } from "formik";

export type RtspInputParamsFormValues = {
  latency: number | "";
  user: string;
  password: string;
  forceTcp: boolean;
};

export const createDefaultRtspInputParams = (): RtspInputParamsFormValues => ({
  latency: 2000,
  user: "",
  password: "",
  forceTcp: false
});

type RtspInputParamsProps = {
  name?: string;
  mode?: "view" | "edit";
};

const RtspInputParams = ({ name = "rtsp", mode = "edit" }: RtspInputParamsProps) => {
  const readOnly = mode === "view";
  const [showPassword, setShowPassword] = useState(false);

  const [latencyField, latencyMeta, latencyHelpers] = useField<number | "">({
    name: `${name}.latency`
  });
  const [userField, userMeta, userHelpers] = useField<string>({
    name: `${name}.user`
  });
  const [passwordField, passwordMeta, passwordHelpers] = useField<string>({
    name: `${name}.password`
  });
  const [forceTcpField, , forceTcpHelpers] = useField<boolean>({
    name: `${name}.forceTcp`
  });

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          {...userField}
          label="User"
          value={userField.value}
          onChange={(event) => userHelpers.setValue(event.target.value.trim())}
          error={Boolean(userMeta.touched && userMeta.error)}
          helperText={userMeta.touched ? userMeta.error : undefined}
          disabled={readOnly}
          fullWidth
          sx={{ flexBasis: { sm: "50%" }, flexGrow: 1 }}
        />
        <TextField
          {...passwordField}
          label="Password"
          type={showPassword ? "text" : "password"}
          value={passwordField.value}
          onChange={(event) => passwordHelpers.setValue(event.target.value.trim())}
          error={Boolean(passwordMeta.touched && passwordMeta.error)}
          helperText={passwordMeta.touched ? passwordMeta.error : undefined}
          disabled={readOnly}
          fullWidth
          sx={{ flexBasis: { sm: "50%" }, flexGrow: 1 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((prev) => !prev)}
                  edge="end"
                  size="small"
                  disabled={readOnly}
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
        <TextField
          {...latencyField}
          label="Latency (ms)"
          type="number"
          value={latencyField.value}
          onChange={(event) => {
            const nextValue = event.target.value;
            latencyHelpers.setValue(nextValue === "" ? "" : Number(nextValue));
          }}
          error={Boolean(latencyMeta.touched && latencyMeta.error)}
          helperText={latencyMeta.touched ? latencyMeta.error : undefined}
          disabled={readOnly}
          slotProps={{ htmlInput: { min: 0, step: 1 } }}
          fullWidth
          sx={{ flexBasis: { sm: "70%" }, flexGrow: 1 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={forceTcpField.value}
              onChange={(event) => forceTcpHelpers.setValue(event.target.checked)}
              name={forceTcpField.name}
              disabled={readOnly}
            />
          }
          label="Force TCP"
          sx={{ flexBasis: { sm: "30%" }, m: 0 }}
        />
      </Stack>
    </Stack>
  );
};

export default RtspInputParams;
